import { onlineManager } from '@tanstack/react-query';
import { Badge, Button, IconButton, Textarea } from '@artosera/ui';
import {
  CalendarRange,
  ChartNoAxesColumn,
  Contact,
  Copy,
  Download,
  FileText,
  Image as ImageIcon,
  Mail,
  Mic,
  MicOff,
  Receipt,
  Send,
  Sparkles,
  ThumbsDown,
  ThumbsUp,
  Users,
  Volume2,
  VolumeX,
} from 'lucide-react';
import { useEffect, useId, useRef, useState, useSyncExternalStore, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import type { TYPES_SOURCE_RECHERCHE } from '@artosera/shared';
import { QUESTION_RECHERCHE_MAX } from '@artosera/shared';
import {
  echecDeQuestion,
  usePoserQuestion,
  useReglagesRecherche,
  useRetourQuestion,
  type EchecQuestion,
  type Echange,
  type MotifRetour,
  type ReponseQuestion,
} from '../../api/assistantRecherche.js';
import { estErreurStepUpRequis, useTelechargerDocument } from '../../api/coffre.js';
import { ouvrirUrlSignee } from '../../api/client.js';
import { estApiError } from '../../api/errors.js';
import { galerieAModule, galerieALaction } from '../../auth/sujet.js';
import type { GalerieSession } from '../../api/types.js';
import { useLangue } from '../../composants/useLangue.js';
import { useDictee } from './useDictee.js';
import styles from './PanneauDemander.module.css';

type TypeSource = (typeof TYPES_SOURCE_RECHERCHE)[number];
type Source = ReponseQuestion['sources'][number];

const ICONES: Record<TypeSource, typeof ImageIcon> = {
  oeuvre: ImageIcon,
  artiste: Users,
  contact: Contact,
  demande: Mail,
  exposition: CalendarRange,
  facture: Receipt,
  affaire: ChartNoAxesColumn,
  document: FileText,
};

/** Suggestions de départ : une par module actif que la personne peut lire. */
const SUGGESTIONS = [
  { cle: 'oeuvres', module: 'oeuvres', action: 'artworks:read' },
  { cle: 'contacts', module: 'contacts', action: 'contacts:read' },
  { cle: 'affaires', module: 'pipeline-ventes', action: 'pipeline:read' },
  { cle: 'documents', module: 'coffre', action: 'documents:read' },
  { cle: 'factures', module: 'facturation', action: 'invoices:read' },
  { cle: 'expositions', module: 'expositions', action: 'exhibitions:read' },
] as const;

const HISTORIQUE_MAX = 6;
const ATTENTE_QUOTA_DEFAUT = 30;

interface EchangeAffiche {
  readonly id: number;
  readonly question: string;
  readonly etat: 'chargement' | 'ok' | 'echec';
  readonly reponse?: ReponseQuestion;
  readonly echec?: EchecQuestion;
  readonly utile?: boolean;
  readonly motifEnvoye?: boolean;
}

/** Un lien de source vient du serveur : on ne le suit que s'il reste dans la galerie. */
function lienSur(lien: string): string | null {
  return /^\/(?!\/)[A-Za-z0-9\-_/]*(\?[A-Za-z0-9\-_=&%]*)?$/.test(lien) ? lien : null;
}

function surLigne(): boolean {
  return onlineManager.isOnline();
}

export interface PanneauDemanderProps {
  readonly galerie: GalerieSession;
  readonly galerieSlug: string;
  /** Appelé quand une source est ouverte : la palette se referme. */
  readonly surNavigation?: () => void;
  /** Pleine page plutôt que palette : le fil des échanges y est plus haut. */
  readonly etendu?: boolean;
}

/**
 * « Demander à Artosera » (add-on 24) : une question, une réponse courte et ses
 * sources. L'échange vit dans cet état seulement : fermer l'écran l'efface, rien
 * n'est écrit dans le navigateur. Les six derniers échanges repartent avec chaque
 * question, le serveur ne garde rien.
 */
export function PanneauDemander({
  galerie,
  galerieSlug,
  surNavigation,
  etendu = false,
}: PanneauDemanderProps): JSX.Element {
  const { t } = useTranslation(['regie', 'commun']);
  const langue = useLangue();
  const idChamp = useId();
  const reglages = useReglagesRecherche(galerieSlug);
  const poser = usePoserQuestion(galerieSlug);
  const retour = useRetourQuestion(galerieSlug);
  const telecharger = useTelechargerDocument(galerieSlug);

  const [saisie, setSaisie] = useState('');
  const [echanges, setEchanges] = useState<readonly EchangeAffiche[]>([]);
  const [restant, setRestant] = useState(0);
  const [lecture, setLecture] = useState<number | null>(null);
  const [copie, setCopie] = useState<number | null>(null);
  const [erreurDocument, setErreurDocument] = useState<string | null>(null);
  const compteur = useRef(0);
  const champ = useRef<HTMLTextAreaElement>(null);
  const finDuFil = useRef<HTMLDivElement>(null);
  const enLigne = useSyncExternalStore((rappel) => onlineManager.subscribe(rappel), surLigne);

  const dictee = useDictee({
    autorisee: reglages.data?.dicteeWeb === true,
    langue,
    // Le texte reconnu s'ajoute au champ, modifiable : jamais d'envoi automatique.
    onTexte: (texte) =>
      setSaisie((avant) => `${avant ? `${avant} ` : ''}${texte}`.slice(0, QUESTION_RECHERCHE_MAX)),
  });

  const coupe = reglages.data?.actif === false;
  const roleFerme =
    reglages.data !== undefined &&
    !(reglages.data.roles as readonly string[]).includes(galerie.role);
  const approfondirOuvert = reglages.data?.approfondir !== false;
  const bloque = coupe || roleFerme || !enLigne;

  // Le fil suit la dernière réponse (absent de jsdom : appel gardé).
  useEffect(() => {
    if (echanges.length > 0) finDuFil.current?.scrollIntoView?.({ block: 'nearest' });
  }, [echanges]);

  // Décompte après un quota dépassé (`Retry-After`).
  useEffect(() => {
    if (restant <= 0) return undefined;
    const minuteur = window.setTimeout(() => setRestant((n) => Math.max(0, n - 1)), 1000);
    return () => window.clearTimeout(minuteur);
  }, [restant]);

  // La synthèse vocale ne survit pas à l'écran.
  useEffect(
    () => () => {
      if (typeof window !== 'undefined' && 'speechSynthesis' in window)
        window.speechSynthesis.cancel();
    },
    [],
  );

  const suggestions = SUGGESTIONS.filter(
    (s) => galerieAModule(galerie, s.module) && galerieALaction(galerie, s.action),
  ).slice(0, 4);

  function miseAJour(id: number, changement: Partial<EchangeAffiche>): void {
    setEchanges((liste) => liste.map((e) => (e.id === id ? { ...e, ...changement } : e)));
  }

  function historiqueAvant(id: number | null): Echange[] {
    const avant =
      id === null
        ? echanges
        : echanges.slice(
            0,
            echanges.findIndex((e) => e.id === id),
          );
    return avant
      .filter((e) => e.etat === 'ok' && e.reponse)
      .slice(-HISTORIQUE_MAX)
      .map((e) => ({ question: e.question, reponse: e.reponse?.reponse ?? '' }));
  }

  function envoyer(question: string, existant: number | null, approfondir: boolean): void {
    const texte = question.trim();
    if (!texte || bloque || restant > 0 || poser.isPending) return;
    const historique = historiqueAvant(existant);
    let id = existant;
    if (id === null) {
      compteur.current += 1;
      id = compteur.current;
      const nouveau: EchangeAffiche = { id, question: texte, etat: 'chargement' };
      setEchanges((liste) => [...liste, nouveau]);
      setSaisie('');
    } else {
      miseAJour(id, { etat: 'chargement', echec: undefined, utile: undefined, motifEnvoye: false });
    }
    const cible = id;
    poser
      .mutateAsync({
        question: texte,
        historique,
        ...(approfondir ? { approfondir: true } : {}),
        cle: crypto.randomUUID(),
      })
      .then((reponse) => {
        miseAJour(cible, { etat: 'ok', reponse });
        champ.current?.focus();
      })
      .catch((erreur: unknown) => {
        const echec = echecDeQuestion(erreur);
        if (echec.nature === 'quota') setRestant(echec.secondes ?? ATTENTE_QUOTA_DEFAUT);
        miseAJour(cible, { etat: 'echec', echec });
      });
  }

  function noter(echange: EchangeAffiche, utile: boolean, motif?: MotifRetour): void {
    const appelId = echange.reponse?.appelId;
    if (!appelId) return;
    miseAJour(echange.id, { utile, ...(motif ? { motifEnvoye: true } : {}) });
    retour.mutate({ appelId, utile, ...(motif ? { motif } : {}) });
  }

  function copier(echange: EchangeAffiche): void {
    const texte = echange.reponse?.reponse;
    if (!texte) return;
    void navigator.clipboard
      .writeText(texte)
      .then(() => setCopie(echange.id))
      .catch(() => undefined);
  }

  function lire(echange: EchangeAffiche): void {
    const texte = echange.reponse?.reponse;
    if (!texte || !('speechSynthesis' in window)) return;
    window.speechSynthesis.cancel();
    if (lecture === echange.id) {
      setLecture(null);
      return;
    }
    const enonce = new SpeechSynthesisUtterance(texte);
    enonce.lang = langue === 'en' ? 'en-US' : 'fr-FR';
    enonce.onend = () => setLecture((courant) => (courant === echange.id ? null : courant));
    setLecture(echange.id);
    window.speechSynthesis.speak(enonce);
  }

  async function telechargerDocument(id: string): Promise<void> {
    setErreurDocument(null);
    try {
      const reponse = await telecharger.mutateAsync({ id });
      ouvrirUrlSignee(reponse.telechargement.url);
    } catch (erreur) {
      setErreurDocument(
        estErreurStepUpRequis(erreur)
          ? t('assistantRecherche.source.aOuvrirDansLeCoffre')
          : estApiError(erreur)
            ? erreur.message
            : t('commun:erreurReseau'),
      );
    }
  }

  const peutParler = typeof window !== 'undefined' && 'speechSynthesis' in window;
  const messageBloque = coupe
    ? t('assistantRecherche.etats.coupe')
    : roleFerme
      ? t('assistantRecherche.etats.role')
      : !enLigne
        ? t('assistantRecherche.etats.horsLigne')
        : null;

  return (
    <div
      className={`${styles.panneau} ${etendu ? styles.etendu : ''}`}
      data-ui="assistant-recherche"
    >
      {messageBloque ? (
        <p className={styles.bandeau} role="status">
          {messageBloque}
        </p>
      ) : null}

      <div className={styles.fil} aria-live="polite" aria-relevant="additions text">
        {echanges.length === 0 && !messageBloque ? (
          <div className={styles.vide}>
            <Sparkles size={20} strokeWidth={1.5} aria-hidden />
            <p>{t('assistantRecherche.vide')}</p>
            {suggestions.length > 0 ? (
              <ul className={styles.suggestions} aria-label={t('assistantRecherche.suggestions')}>
                {suggestions.map((s) => (
                  <li key={s.cle}>
                    <button
                      type="button"
                      className={styles.suggestion}
                      onClick={() => {
                        setSaisie(t(`assistantRecherche.suggestion.${s.cle}`));
                        champ.current?.focus();
                      }}
                    >
                      {t(`assistantRecherche.suggestion.${s.cle}`)}
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}

        {echanges.map((echange) => (
          <article key={echange.id} className={styles.echange}>
            <p className={styles.question}>
              <span className={styles.masque}>{t('assistantRecherche.vous')}</span>
              {echange.question}
            </p>

            {echange.etat === 'chargement' ? (
              <p className={styles.attente} role="status">
                <span className={styles.spinner} aria-hidden="true" />
                {t('assistantRecherche.cherche')}
              </p>
            ) : null}

            {echange.etat === 'echec' && echange.echec ? (
              <div className={styles.echec} role="alert">
                <p>{messageEchec(echange.echec, restant, t)}</p>
                {echange.echec.nature !== 'plafond' &&
                echange.echec.nature !== 'coupe' &&
                echange.echec.nature !== 'role' ? (
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={restant > 0 || bloque}
                    onClick={() => envoyer(echange.question, echange.id, false)}
                  >
                    {t('assistantRecherche.reessayer')}
                  </Button>
                ) : null}
              </div>
            ) : null}

            {echange.etat === 'ok' && echange.reponse ? (
              <Reponse
                echange={echange}
                reponse={echange.reponse}
                galerieSlug={galerieSlug}
                surNavigation={surNavigation}
                telecharger={(id) => void telechargerDocument(id)}
                telechargementEnCours={telecharger.isPending}
                approfondir={
                  approfondirOuvert && echange.reponse.niveau === 'rapide'
                    ? () => envoyer(echange.question, echange.id, true)
                    : null
                }
                occupe={poser.isPending}
                noter={(utile, motif) => noter(echange, utile, motif)}
                copier={() => copier(echange)}
                copie={copie === echange.id}
                lire={peutParler ? () => lire(echange) : null}
                enLecture={lecture === echange.id}
              />
            ) : null}
          </article>
        ))}
        {erreurDocument ? (
          <p className={styles.erreurDocument} role="alert">
            {erreurDocument}
          </p>
        ) : null}
        <div ref={finDuFil} />
      </div>

      <form
        className={styles.saisie}
        onSubmit={(e) => {
          e.preventDefault();
          envoyer(saisie, null, false);
        }}
      >
        <label htmlFor={idChamp} className={styles.masque}>
          {t('assistantRecherche.champ')}
        </label>
        <Textarea
          id={idChamp}
          ref={champ}
          autoFocus
          rows={2}
          maxLength={QUESTION_RECHERCHE_MAX}
          className={styles.champ}
          placeholder={t('assistantRecherche.placeholder')}
          value={saisie}
          disabled={bloque}
          onChange={(e) => setSaisie(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
              e.preventDefault();
              envoyer(saisie, null, false);
            }
          }}
        />
        <div className={styles.actions}>
          {dictee.disponible ? (
            <IconButton
              variant="outline"
              icon={
                dictee.ecoute ? (
                  <MicOff size={16} strokeWidth={1.5} aria-hidden />
                ) : (
                  <Mic size={16} strokeWidth={1.5} aria-hidden />
                )
              }
              aria-label={t(
                dictee.ecoute ? 'assistantRecherche.dicteeArreter' : 'assistantRecherche.dicter',
              )}
              aria-pressed={dictee.ecoute}
              disabled={bloque}
              onClick={dictee.ecoute ? dictee.arreter : dictee.demarrer}
            />
          ) : null}
          <Button
            type="submit"
            size="sm"
            disabled={bloque || restant > 0 || saisie.trim() === ''}
            loading={poser.isPending}
          >
            <Send size={16} strokeWidth={1.5} aria-hidden />
            {restant > 0
              ? t('assistantRecherche.attendre', { n: restant })
              : t('assistantRecherche.envoyer')}
          </Button>
        </div>
        {dictee.disponible ? (
          <p className={styles.avis}>
            {dictee.ecoute
              ? t('assistantRecherche.dicteeEnCours')
              : t('assistantRecherche.avisDictee')}
            {dictee.erreur ? ` ${t(`assistantRecherche.dicteeErreur.${dictee.erreur}`)}` : ''}
          </p>
        ) : null}
      </form>
    </div>
  );
}

function messageEchec(
  echec: EchecQuestion,
  restant: number,
  t: (cle: string, options?: Record<string, unknown>) => string,
): string {
  if (echec.nature === 'quota')
    return restant > 0
      ? t('assistantRecherche.etats.quotaDecompte', { n: restant })
      : t('assistantRecherche.etats.quota');
  return t(`assistantRecherche.etats.${echec.nature}`);
}

interface ReponseProps {
  readonly echange: EchangeAffiche;
  readonly reponse: ReponseQuestion;
  readonly galerieSlug: string;
  readonly surNavigation?: () => void;
  readonly telecharger: (id: string) => void;
  readonly telechargementEnCours: boolean;
  readonly approfondir: (() => void) | null;
  readonly occupe: boolean;
  readonly noter: (utile: boolean, motif?: MotifRetour) => void;
  readonly copier: () => void;
  readonly copie: boolean;
  readonly lire: (() => void) | null;
  readonly enLecture: boolean;
}

function Reponse({
  echange,
  reponse,
  galerieSlug,
  surNavigation,
  telecharger,
  telechargementEnCours,
  approfondir,
  occupe,
  noter,
  copier,
  copie,
  lire,
  enLecture,
}: ReponseProps): JSX.Element {
  const { t } = useTranslation(['regie', 'commun']);
  const ecran = reponse.ecran ? lienSur(reponse.ecran) : null;
  return (
    <div className={styles.reponse}>
      <p className={styles.texte}>{reponse.reponse}</p>

      {reponse.etapes.length > 0 ? (
        <p className={styles.etapes}>
          {t('assistantRecherche.etapes.intro')}{' '}
          {reponse.etapes
            .map((etape) =>
              etape.etat === 'ok'
                ? t(`assistantRecherche.etapes.outils.${etape.outil}`, {
                    defaultValue: etape.outil,
                  })
                : `${t(`assistantRecherche.etapes.outils.${etape.outil}`, { defaultValue: etape.outil })} (${t(`assistantRecherche.etapes.${etape.etat}`)})`,
            )
            .join(', ')}
          .
        </p>
      ) : null}

      {reponse.masques ? (
        <p className={styles.masques}>
          {t('assistantRecherche.masques', { count: reponse.masques })}
        </p>
      ) : null}

      {ecran ? (
        <Link
          className={styles.ecran}
          to={`/${galerieSlug}${ecran}`}
          onClick={() => surNavigation?.()}
        >
          {t('assistantRecherche.ouvrirEcran')}
        </Link>
      ) : null}

      {reponse.sources.length > 0 ? (
        <ul className={styles.sources} aria-label={t('assistantRecherche.sources')}>
          {reponse.sources.map((source) => (
            <SourceListe
              key={`${source.type}:${source.id}`}
              source={source}
              galerieSlug={galerieSlug}
              surNavigation={surNavigation}
              telecharger={telecharger}
              telechargementEnCours={telechargementEnCours}
            />
          ))}
        </ul>
      ) : null}

      <div className={styles.outils}>
        {reponse.niveau === 'approfondi' ? (
          <Badge tone="neutral">{t('assistantRecherche.approfondi')}</Badge>
        ) : null}
        {approfondir ? (
          <Button variant="ghost" size="sm" disabled={occupe} onClick={approfondir}>
            {t('assistantRecherche.approfondir')}
          </Button>
        ) : null}
        <IconButton
          size="sm"
          icon={<ThumbsUp size={16} strokeWidth={1.5} aria-hidden />}
          aria-label={t('assistantRecherche.utile')}
          aria-pressed={echange.utile === true}
          onClick={() => noter(true)}
        />
        <IconButton
          size="sm"
          icon={<ThumbsDown size={16} strokeWidth={1.5} aria-hidden />}
          aria-label={t('assistantRecherche.inutile')}
          aria-pressed={echange.utile === false}
          onClick={() => noter(false)}
        />
        <IconButton
          size="sm"
          icon={<Copy size={16} strokeWidth={1.5} aria-hidden />}
          aria-label={t('assistantRecherche.copier')}
          onClick={copier}
        />
        {lire ? (
          <IconButton
            size="sm"
            icon={
              enLecture ? (
                <VolumeX size={16} strokeWidth={1.5} aria-hidden />
              ) : (
                <Volume2 size={16} strokeWidth={1.5} aria-hidden />
              )
            }
            aria-label={t(enLecture ? 'assistantRecherche.lireArreter' : 'assistantRecherche.lire')}
            aria-pressed={enLecture}
            onClick={lire}
          />
        ) : null}
        <span className={styles.confirmation} role="status">
          {copie ? t('assistantRecherche.copie') : ''}
          {echange.utile === true ? t('assistantRecherche.merci') : ''}
        </span>
      </div>

      {echange.utile === false && !echange.motifEnvoye ? (
        <div
          className={styles.motifs}
          role="group"
          aria-label={t('assistantRecherche.motif.titre')}
        >
          <span>{t('assistantRecherche.motif.titre')}</span>
          {(['faux', 'incomplet', 'lent', 'autre'] as const).map((motif) => (
            <Button key={motif} variant="ghost" size="sm" onClick={() => noter(false, motif)}>
              {t(`assistantRecherche.motif.${motif}`)}
            </Button>
          ))}
        </div>
      ) : null}
      {echange.utile === false && echange.motifEnvoye ? (
        <p className={styles.confirmation} role="status">
          {t('assistantRecherche.merci')}
        </p>
      ) : null}
    </div>
  );
}

function SourceListe({
  source,
  galerieSlug,
  surNavigation,
  telecharger,
  telechargementEnCours,
}: {
  readonly source: Source;
  readonly galerieSlug: string;
  readonly surNavigation?: () => void;
  readonly telecharger: (id: string) => void;
  readonly telechargementEnCours: boolean;
}): JSX.Element | null {
  const { t } = useTranslation('regie');
  const lien = lienSur(source.lien);
  if (!lien) return null;
  const Icone = ICONES[source.type];
  const cible = `/${galerieSlug}${lien}`;
  if (source.type !== 'document')
    return (
      <li>
        <Link className={styles.puce} to={cible} onClick={() => surNavigation?.()}>
          <Icone size={14} strokeWidth={1.5} aria-hidden />
          <span className={styles.puceLibelle}>{source.libelle}</span>
        </Link>
      </li>
    );
  return (
    <li className={styles.puceDocument}>
      <span className={styles.puce}>
        <Icone size={14} strokeWidth={1.5} aria-hidden />
        <span className={styles.puceLibelle}>{source.libelle}</span>
      </span>
      <Link className={styles.lienPuce} to={cible} onClick={() => surNavigation?.()}>
        {t('assistantRecherche.source.ouvrir')}{' '}
        <span className={styles.masque}>{source.libelle}</span>
      </Link>
      <button
        type="button"
        className={styles.lienPuce}
        disabled={telechargementEnCours}
        onClick={() => telecharger(source.id)}
      >
        <Download size={14} strokeWidth={1.5} aria-hidden />
        {t('assistantRecherche.source.telecharger')}{' '}
        <span className={styles.masque}>{source.libelle}</span>
      </button>
    </li>
  );
}
